import { useMemo } from "react";
import {
  COST_SWR_BASE_CONFIG,
  appendDateRangeParams,
  useCostAuthHeaders,
  VITE_COST_AND_USAGE_API,
} from "rwb/pages/project/hooks/Shared/useCosts";
import useSWR from "swr";
import { camelizingFetcher } from "~/hooks/useFetcher";
import { isApiError } from "~/utils/apiClient";
import { MonthlyProjectCost, monthRanges, ProjectCostResponse } from "./costSummary";

const projectCostUrl = (projectUuid: string, startDate: string, endDate: string) => {
  const params = new URLSearchParams();
  appendDateRangeParams(params, startDate, endDate);
  return `${VITE_COST_AND_USAGE_API}/v1/projects/${projectUuid}/cost?${params}`;
};

/**
 * Project cost per calendar month for the last `months` months (current month
 * included), one cost-service call per month since the endpoint only returns a
 * total over a date range. Pass a null `projectUuid` to skip fetching. The
 * endpoint takes the project UUID, not its short id.
 */
export const useProjectMonthlyCosts = (projectUuid: string | null, months: number) => {
  const headers = useCostAuthHeaders();
  const ranges = useMemo(() => monthRanges(months, new Date()), [months]);
  const urls = projectUuid
    ? ranges.map(({ startDate, endDate }) => projectCostUrl(projectUuid, startDate, endDate))
    : null;

  return useSWR<MonthlyProjectCost[]>(
    urls && ["project-monthly-costs", ...urls],
    async ([, ...monthUrls]: string[]) => {
      const fetchCost = camelizingFetcher<ProjectCostResponse>({}, { headers });
      return Promise.all(
        monthUrls.map(async (url, i) => {
          try {
            return { month: ranges[i].month, cost: await fetchCost(url) };
          } catch (error) {
            // The service answers 404 for a window with no cost data; the
            // caller is a confirmed owner, so that just means an empty month.
            if (isApiError(error) && error.status === 404) {
              return { month: ranges[i].month, cost: null };
            }
            throw error;
          }
        })
      );
    },
    COST_SWR_BASE_CONFIG
  );
};
